import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { gamesAPI } from '../services/api';
import { Gamepad2, Settings, Plus, Filter, BarChart3, Crown } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import GameGrid from '../components/games/GameGrid';
import GameStats from '../components/games/GameStats';
import { SUBSCRIPTION_TIERS, getSubscriptionTierName, getSubscriptionTierBadgeColor } from '../constants/games';
import toast from 'react-hot-toast';

const Games = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedGame, setSelectedGame] = useState(null);
  const [difficulty, setDifficulty] = useState('medium');
  const [showStats, setShowStats] = useState(false);
  const [selectedGameForStats, setSelectedGameForStats] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'free', 'pro', 'enterprise'

  // Get user subscription tier (default to free if not available)
  const userSubscription = user?.subscription?.planType || SUBSCRIPTION_TIERS.FREE;

  const { data: availableGames, isLoading: gamesLoading } = useQuery(
    'availableGames',
    () => gamesAPI.getAvailableGames().then(res => res.data.games),
    {
      onSuccess: (games) => {
        console.log(`Loaded ${games.length} games from server`);
      }
    }
  );

  const { data: activeGames, isLoading: activeLoading } = useQuery(
    'activeGames',
    () => gamesAPI.getActiveGames().then(res => res.data.games),
    { enabled: !!user }
  );

  const createGameMutation = useMutation(
    (gameData) => gamesAPI.createGame(gameData),
    {
      onSuccess: (response) => {
        toast.success('Game created successfully!');
        queryClient.invalidateQueries('activeGames');
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || 'Failed to create game';
        toast.error(errorMessage);
        
        // Handle subscription upgrade prompts
        if (error.response?.data?.upgradeRequired) {
          // Could show upgrade modal here
          console.log('Upgrade required:', error.response.data);
        }
      }
    }
  );

  const handleCreateGame = (gameId) => {
    if (!user) {
      toast.error('Please login to play games');
      return;
    }

    createGameMutation.mutate({
      type: gameId,
      difficulty: difficulty
    });
  };

  const handleShowStats = (game) => {
    setSelectedGameForStats(game);
    setShowStats(true);
  };

  // Get games by subscription tier for display
  const getGamesByTier = (tier) => {
    return availableGames?.filter(game => game.subscriptionTier === tier) || [];
  };

  const freeGames = getGamesByTier(SUBSCRIPTION_TIERS.FREE);
  const proGames = getGamesByTier(SUBSCRIPTION_TIERS.PRO);
  const enterpriseGames = getGamesByTier(SUBSCRIPTION_TIERS.ENTERPRISE);

  // Get filtered games based on active tab
  const getFilteredGames = () => {
    switch (activeTab) {
      case 'free':
        return freeGames;
      case 'pro':
        return proGames;
      case 'enterprise':
        return enterpriseGames;
      default:
        return availableGames || [];
    }
  };

  if (gamesLoading || activeLoading) {
    return <LoadingSpinner size="xl" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-discord-lightest mb-2 flex items-center">
              <Gamepad2 className="h-8 w-8 mr-3" />
              Games Library
            </h1>
            <p className="text-discord-light">
              Choose from our collection of interactive games. Some games require premium subscriptions.
            </p>
          </div>
          
          {user && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Crown className="h-4 w-4 text-yellow-400" />
                <span className={`badge ${getSubscriptionTierBadgeColor(userSubscription)}`}>
                  {getSubscriptionTierName(userSubscription)}
                </span>
              </div>
              <div className="text-sm text-discord-light">
                Games today: {user.gameStats?.gamesToday || 0}
                {user.subscription?.type === 'free' && '/10'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Tiers Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-discord-lightest">Free Games</h3>
              <p className="text-sm text-discord-light">{freeGames.length} games available</p>
            </div>
            <div className="text-2xl">🆓</div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-discord-lightest">Pro Games</h3>
              <p className="text-sm text-discord-light">{proGames.length} games available</p>
            </div>
            <div className="text-2xl">⭐</div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-discord-lightest">Enterprise Games</h3>
              <p className="text-sm text-discord-light">{enterpriseGames.length} games available</p>
            </div>
            <div className="text-2xl">🏢</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="card">
        <div className="tabs tabs-boxed">
          <button 
            className={`tab ${activeTab === 'all' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Games ({availableGames?.length || 0})
          </button>
          <button 
            className={`tab ${activeTab === 'free' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('free')}
          >
            Free ({freeGames.length})
          </button>
          <button 
            className={`tab ${activeTab === 'pro' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('pro')}
          >
            Pro ({proGames.length})
          </button>
          <button 
            className={`tab ${activeTab === 'enterprise' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('enterprise')}
          >
            Enterprise ({enterpriseGames.length})
          </button>
        </div>
      </div>

      {/* Games Grid */}
      <GameGrid
        games={getFilteredGames()}
        userSubscription={userSubscription}
        onCreateGame={handleCreateGame}
        isLoading={createGameMutation.isLoading}
      />

      {/* Active Games Section */}
      {activeGames && activeGames.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold text-discord-lightest mb-4 flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Your Active Games
          </h2>
          <div className="space-y-3">
            {activeGames.map((game) => (
              <div key={game._id} className="flex items-center justify-between p-4 bg-discord-dark rounded-lg">
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">
                    {game.type === 'tic-tac-toe' ? '❌⭕' :
                     game.type === 'number-guess' ? '🔢' :
                     game.type === 'rock-paper-scissors' ? '🪨📄✂️' : '🎮'}
                  </span>
                  <div>
                    <p className="text-discord-lightest font-medium">
                      {game.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    <p className="text-sm text-discord-light">
                      {game.players?.length || 0} players • {game.status}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setSelectedGame(game)}
                    className="btn btn-sm btn-primary"
                  >
                    Continue
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Game Statistics Modal */}
      {showStats && selectedGameForStats && (
        <div className="modal modal-open">
          <div className="modal-box max-w-6xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-discord-lightest">
                Game Statistics
              </h3>
              <button 
                className="btn btn-sm btn-circle btn-ghost"
                onClick={() => setShowStats(false)}
              >
                ✕
              </button>
            </div>
            
            <GameStats
              gameInfo={selectedGameForStats}
              stats={selectedGameForStats.stats || {}}
            />
            
            <div className="modal-action">
              <button 
                className="btn"
                onClick={() => setShowStats(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Games;